/**
 * Writing a card's table changes into the live Word document through Word's
 * own table API under Track Changes.
 *
 * A paragraph package goes in through `insertOoxml(replace)`, but Word does
 * not replace a table that way (`document.ts` deletes tables before such a
 * write), so whole-row changes take the second door: `TableRow.insertRows`
 * clones the row it is called on, `TableRow.delete` strikes one, and
 * `Paragraph.insertTable` builds a new table, each with change tracking on so
 * Word writes the row marks, the paragraph marks and the run wrappers itself,
 * and the row's author is the signed-in Word user.
 *
 * Nothing here names a paragraph. The add-in's paragraph-naming pass names
 * the new cell paragraphs, as it names every new paragraph, before the next
 * upload.
 *
 * `merged_cells` is honoured by the stored-file writer; here Word decides
 * what a new row does under a vertical merge. The harness probe records what
 * it does in Word Online and on desktop.
 */
import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import type { TableOperationsApplyResult } from "@common/utils/redline/table-operations";

import { withTemporaryTrackingMode } from "../../document";
import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE } from "../../errors";

/** Word's `changeTrackingMode` value that tracks every author's edits; the string form works in Word Online and on desktop without the `Word` global. */
export const TRACK_ALL_CHANGES = "TrackAll";
/** Word's `InsertLocation` for "right after this row or paragraph", in its string form. */
const INSERT_AFTER = "After";
/** Word's `InsertLocation` for "at the end of this body", the form `Body.insertTable` takes; a cell's body ends where its last paragraph does. */
const INSERT_AT_END = "End";

/** The Word objects one bookmark resolves to, loaded together so one sync answers every anchor; the cell is read off the paragraph after that sync. */
interface BookmarkAnchor {
    range: Word.Range;
    paragraph: Word.Paragraph;
}

export interface TableChangeWriteOutcome {
    /** Rows inserted plus rows deleted plus tables inserted. */
    changeCount: number;
}

export class TableChangeWriter {
    /**
     * Writes every table change of `edits` in one tracking-mode window: one
     * sync resolves every anchor, one sync writes the row deletions and
     * insertions, then each new table is placed with its own sync because its
     * anchor may first have to be walked up out of a table. Deletions go first,
     * because a struck row keeps its place.
     */
    static async write(context: Word.RequestContext, edits: TableOperationsApplyResult): Promise<TableChangeWriteOutcome> {
        let changeCount = 0;
        await withTemporaryTrackingMode(context, TRACK_ALL_CHANGES, async () => {
            const anchors = TableChangeWriter.queueAnchorReads(context, edits);
            await context.sync();
            for (const deletion of edits.rowDeletions) {
                TableChangeWriter.rowOf(anchors, deletion.rowOfBookmark).delete();
                changeCount += 1;
            }
            for (const insertion of edits.rowInsertions) {
                /**
                 * EXAMPLE
                 *   rows = [["Annual uplift cap", "percentage of prior-year fee", "3%"]]
                 *   -> insertRows("After", 1, rows): one row cloned from the template, its cells filled left to right
                 */
                TableChangeWriter.rowOf(anchors, insertion.afterRowOfBookmark).insertRows(INSERT_AFTER, insertion.rows.length, insertion.rows);
                changeCount += insertion.rows.length;
            }
            await context.sync();
            for (const tableInsertion of edits.tableInsertions) {
                const anchorParagraph = TableChangeWriter.paragraphOf(anchors, tableInsertion.afterBookmark);
                const anchorCell = TableChangeWriter.cellOf(anchors, tableInsertion.afterBookmark);
                const enclosingTable = tableInsertion.place === "after_table" ? await TableChangeWriter.outermostTableOf(context, anchorParagraph) : null;
                const rowCount = tableInsertion.rows.length;
                const columnCount = tableInsertion.rows[0].length;
                /**
                 * CASES, for where the new table goes (`TablePlacement`)
                 *   - "after_paragraph" from a cell paragraph -> the end of that cell's own body, so the nested table
                 *                                                is placed by naming the cell. `Paragraph.insertTable("After")`
                 *                                                on the cell paragraph put the table one cell to the right
                 *                                                (Schedule 1 fees table: "per tenant" asked, "$480,000" written)
                 *   - "after_paragraph" from a body paragraph -> right after the paragraph itself
                 *   - "after_table" from a cell               -> an empty paragraph inserted after the outermost table
                 *                                                holding it, so the new table lands after that table
                 *                                                and the two tables stay apart
                 *
                 * EXAMPLE
                 *   rows = [["Component", "Unit"], ["Platform", "per year"]]
                 *   -> insertTable(2, 2, "End" | "After", rows): two rows, two columns, Word's default table style
                 */
                if (enclosingTable === null && anchorCell !== null) {
                    anchorCell.body.insertTable(rowCount, columnCount, INSERT_AT_END, tableInsertion.rows);
                } else {
                    const placeAfter = enclosingTable === null ? anchorParagraph : enclosingTable.insertParagraph("", INSERT_AFTER);
                    placeAfter.insertTable(rowCount, columnCount, INSERT_AFTER, tableInsertion.rows);
                }
                await context.sync();
                changeCount += 1;
            }
        });
        return { changeCount };
    }

    /**
     * Queues, for every bookmark the changes name, the reads that tell whether
     * the bookmark exists and whether its first paragraph sits in a table cell.
     * Nothing is known until the caller syncs.
     *
     * WHEN the bookmark is gone, so `getBookmarkRangeOrNullObject` answers a null object
     *   CONSEQUENCE
     *     `getFirstOrNullObject` queues a read that resolves to a null object too,
     *     so the sync succeeds and `rowOf` and `paragraphOf` reach their own
     *     `range.isNullObject` guards and fail as APPLY_FAILURE_LOCATION_NOT_FOUND.
     *     `getFirst` would instead throw ItemNotFound inside the sync, before any
     *     guard runs, taking down every other change queued in the same batch.
     */
    private static queueAnchorReads(context: Word.RequestContext, edits: TableOperationsApplyResult): Map<string, BookmarkAnchor> {
        const anchors = new Map<string, BookmarkAnchor>();
        const bookmarks = [...edits.rowDeletions.map((deletion) => deletion.rowOfBookmark), ...edits.rowInsertions.map((insertion) => insertion.afterRowOfBookmark), ...edits.tableInsertions.map((tableInsertion) => tableInsertion.afterBookmark)];
        for (const bookmark of bookmarks) {
            if (anchors.has(bookmark)) continue;
            const range = context.document.getBookmarkRangeOrNullObject(bookmark);
            range.load("isNullObject");
            const paragraph = range.paragraphs.getFirstOrNullObject();
            paragraph.load("parentTableCellOrNullObject/isNullObject");
            anchors.set(bookmark, { range, paragraph });
        }
        return anchors;
    }

    /**
     * The outermost table holding a paragraph, or null for a body paragraph.
     * Each step up is one sync; nesting deeper than two never occurs in the
     * corpus, so this stays two round trips at most.
     */
    private static async outermostTableOf(context: Word.RequestContext, paragraph: Word.Paragraph): Promise<Word.Table | null> {
        let table = paragraph.parentTableOrNullObject;
        table.load("isNullObject");
        await context.sync();
        if (table.isNullObject) return null;
        for (;;) {
            const parentTable = table.parentTableOrNullObject;
            parentTable.load("isNullObject");
            await context.sync();
            if (parentTable.isNullObject) return table;
            table = parentTable;
        }
    }

    /**
     * WHEN the bookmark is gone, or its paragraph is no longer inside a table cell
     *   CONSEQUENCE
     *     Fail as location not found: the document moved on since the card was made, and the user regenerates.
     */
    /**
     * The table cell holding the bookmark's first paragraph.
     *
     * NULL
     *   The paragraph is body text outside any table, so a new table follows the
     *   paragraph rather than filling a cell.
     */
    private static cellOf(anchors: Map<string, BookmarkAnchor>, bookmark: string): Word.TableCell | null {
        const anchor = anchors.get(bookmark);
        if (anchor === undefined || anchor.range.isNullObject) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
        const cell = anchor.paragraph.parentTableCellOrNullObject;
        return cell.isNullObject ? null : cell;
    }

    private static rowOf(anchors: Map<string, BookmarkAnchor>, bookmark: string): Word.TableRow {
        const anchor = anchors.get(bookmark);
        if (anchor === undefined || anchor.range.isNullObject) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
        const cell = anchor.paragraph.parentTableCellOrNullObject;
        if (cell.isNullObject) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
        return cell.parentRow;
    }

    /**
     * WHEN no bookmark of that name exists in the document
     *   CONSEQUENCE
     *     Fail as location not found.
     */
    private static paragraphOf(anchors: Map<string, BookmarkAnchor>, bookmark: string): Word.Paragraph {
        const anchor = anchors.get(bookmark);
        if (anchor === undefined || anchor.range.isNullObject) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
        return anchor.paragraph;
    }
}
